import {
  Modal,
  TextField,
  Text,
  InlineStack,
  BlockStack,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {useToast} from 'utilities/toasts';
import {useState} from 'react';
import {useMutation} from '@shopify/react-graphql';
import {useShop} from '@web-utilities/shop';

import AppRequestSupportMutation from './graphql/RequestAppSupport.graphql';
import {AppIcon} from './images';
import styles from './FeedbackModal.scss';

interface Props {
  open: boolean;
  onSubmit(): void;
  onCancel(): void;
}

const MIN_CHARACTER_COUNT = 30;

export function FeedbackModal({open, onSubmit, onCancel}: Props) {
  const [i18n] = useI18n();
  const toast = useToast();
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const {shopEmail} = useShop();
  const submitFeedback = useMutation(AppRequestSupportMutation);
  const [feedbackValue, setFeedbackValue] = useState('');

  const handleFeedbackValueChange = (newValue: string) =>
    setFeedbackValue(newValue);

  return (
    <Modal
      title={i18n.translate('FeedbackModal.title')}
      open={open}
      onClose={onCancel}
      primaryAction={{
        content: i18n.translate('FeedbackModal.actions.submit'),
        onAction: async () => {
          try {
            setSubmittingFeedback(true);
            const response = await submitFeedback({
              variables: {
                body: feedbackValue,
              },
            });

            if (
              !response ||
              !response.data ||
              !response.data.requestAppSupport ||
              response?.data?.requestAppSupport?.userErrors?.length > 0
            ) {
              throw new Error();
            }

            toast.show({
              content: i18n.translate('FeedbackModal.toastSuccess'),
              error: false,
            });
            setFeedbackValue('');
            onSubmit();
          } catch {
            toast.show({
              content: i18n.translate('FeedbackModal.toastError'),
              error: true,
            });
          }
          setSubmittingFeedback(false);
        },
        disabled: feedbackValue.length < MIN_CHARACTER_COUNT,
        loading: submittingFeedback,
      }}
      secondaryActions={[
        {
          content: i18n.translate('Common.cancel'),
          onAction: onCancel,
        },
      ]}
    >
      <Modal.Section>
        <InlineStack wrap={false} gap="400">
          <div className={styles.AppIcon}>
            <img src={AppIcon} alt={i18n.translate('Common.appName')} />
          </div>
          <BlockStack>
            <p>
              <Text variant="bodyMd" fontWeight="bold" as="span">
                {i18n.translate('Common.appName')}
              </Text>
            </p>
            <p>
              <Text variant="bodyMd" tone="subdued" as="span">
                {i18n.translate('FeedbackModal.byShopify')}
              </Text>
            </p>
          </BlockStack>
        </InlineStack>
        <p>
          {i18n.translate('FeedbackModal.support', {
            appTitle: i18n.translate('Common.appName'),
          })}
        </p>
      </Modal.Section>
      <Modal.Section>
        <BlockStack gap="400">
          <div className={styles.GetSupportTextFieldSection}>
            <TextField
              autoComplete="off"
              type="text"
              value={feedbackValue}
              onChange={handleFeedbackValueChange}
              label={i18n.translate('FeedbackModal.feedbackLabel')}
              placeholder={i18n.translate('FeedbackModal.placeholder')}
              multiline={3}
              spellCheck
            />
            <InlineStack align="space-between">
              <Text variant="bodyMd" tone="subdued" as="span">
                {i18n.translate('FeedbackModal.textareaMinimum')}
              </Text>
              <Text variant="bodyMd" tone="subdued" as="span">
                {feedbackValue.length}
              </Text>
            </InlineStack>
          </div>
          <p>
            {i18n.translate('FeedbackModal.responseDisclaimer', {
              accountEmail: shopEmail,
            })}
          </p>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
